import { useCallback, useEffect, useState } from "react";
import { api, apiErrorMessage } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, TrendingUp, TrendingDown, RefreshCw, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
const monthLabel = (mk) => {
  const [y, m] = (mk || "").split("-");
  if (!y || !m) return mk || "—";
  return new Date(Number(y), Number(m) - 1, 1).toLocaleString(undefined, { month: "short", year: "numeric" });
};

const SEV_TONE = {
  critical: "border-red-500/40 text-red-300 bg-red-500/[0.08]",
  warning:  "border-amber-500/40 text-amber-300 bg-amber-500/[0.08]",
  info:     "border-zinc-500/40 text-zinc-300 bg-white/[0.03]",
};

/**
 * ExpenseAnomaliesCard — auto-flag any category moving >25% month over month.
 * Reads /api/admin/expenses/anomalies. Empty state = "no cost creep detected".
 */
export function ExpenseAnomaliesCard({ scope = "all", threshold = 0.25 }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { threshold, months: 6 };
      if (scope && scope !== "all") params.property_id = scope;
      const { data } = await api.get("/admin/expenses/anomalies", { params });
      setData(data);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load anomalies");
    } finally {
      setLoading(false);
    }
  }, [scope, threshold]);

  useEffect(() => { load(); }, [load]);

  const flags = data?.items || [];
  const critical = flags.filter((f) => f.severity === "critical").length;

  return (
    <Card className="p-5 border-white/10 bg-card" data-testid="expense-anomalies-card">
      <div className="flex items-start justify-between mb-3">
        <div>
          <div className="overline">Anomaly detection</div>
          <div className="text-base font-semibold font-display mt-0.5 inline-flex items-center gap-2">
            <AlertTriangle className={"h-4 w-4 " + (critical > 0 ? "text-red-400" : "text-amber-400")} />
            Expense creep watch
          </div>
          <div className="text-xs text-zinc-500 mt-1">
            Any category jumping over {(threshold * 100).toFixed(0)}% month-over-month is flagged so
            you catch cost creep before it hits the reserve.
          </div>
        </div>
        <div className="flex items-center gap-2">
          {flags.length > 0 ? (
            <Badge variant="outline" className="border-amber-500/40 text-amber-300 tabular-nums" data-testid="anomaly-count">
              {flags.length} flag{flags.length === 1 ? "" : "s"}
              {critical > 0 && <span className="ml-1 text-red-300">· {critical} critical</span>}
            </Badge>
          ) : (
            <Badge variant="outline" className="border-emerald-500/40 text-emerald-300" data-testid="anomaly-none">
              <ShieldCheck className="h-3 w-3 mr-1" /> All clear
            </Badge>
          )}
          <Button variant="ghost" size="sm" onClick={load} disabled={loading} className="h-7 px-2" data-testid="anomalies-refresh">
            <RefreshCw className={"h-3 w-3 " + (loading ? "animate-spin" : "")} />
          </Button>
        </div>
      </div>

      {flags.length === 0 ? (
        <div className="rounded-md border border-dashed border-white/10 p-6 text-center text-xs text-emerald-300" data-testid="anomalies-empty">
          <ShieldCheck className="h-4 w-4 mx-auto mb-1" />
          No cost creep detected over the last {data?.months_analyzed?.length || 6} months.
        </div>
      ) : (
        <div className="space-y-1.5">
          {flags.map((f, idx) => {
            const Arrow = f.direction === "up" ? TrendingUp : TrendingDown;
            return (
              <div
                key={`${f.category}-${f.month}-${idx}`}
                className={"flex items-center justify-between gap-2 rounded-md border px-3 py-2 text-xs " + (SEV_TONE[f.severity] || SEV_TONE.info)}
                data-testid={`anomaly-row-${f.category}-${f.month}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-zinc-100">
                    <span className="font-semibold capitalize">{f.category.replace(/_/g, " ")}</span>
                    <span className="text-zinc-500">·</span>
                    <span className="text-zinc-400">{monthLabel(f.month)}</span>
                  </div>
                  <div className="text-[11px] text-zinc-400 mt-0.5">
                    {money(f.prior_amount)} → {money(f.current_amount)}
                    {" · "}
                    <span className={f.direction === "up" ? "text-red-300" : "text-emerald-300"}>
                      {f.delta > 0 ? "+" : ""}{money(f.delta)} vs {monthLabel(f.prior_month)}
                    </span>
                  </div>
                </div>
                <Badge variant="outline"
                       className={"tabular-nums shrink-0 " +
                         (f.direction === "up" ? "border-red-500/40 text-red-300" : "border-emerald-500/40 text-emerald-300")}>
                  <Arrow className="h-3 w-3 mr-0.5" />
                  {f.pct_change > 0 ? "+" : ""}{(f.pct_change * 100).toFixed(0)}%
                </Badge>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

export default ExpenseAnomaliesCard;
